import React, { useRef } from 'react';
import BRAND from '../brandConfig';
import { useApp } from '../context/AppContext';
import { useI18n } from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ThemeToggle from '../components/ThemeToggle';
import Wordmark from '../components/Wordmark';
import StandardIcon from '../components/StandardIcons';

// ═══════════════════════════════════════════════════════════════════════════
//  THE PUBLIC HOMEPAGE
//
//  A recruiting page, not a portal screen. On launch day the roster is empty,
//  so this sells to TRADES: a silver rail, a dark banner carrying the offer,
//  the stat rail that is the actual argument, why pros stay, the trades we
//  place, who is on the platform, how to get on it, and a closing ask.
//
//  Layout only — every word, photo and figure comes from `BRAND.site` and
//  `BRAND.stats` in brandConfig.js, so the page is re-worded there, never
//  here. The provenance of each public number is documented at the top of
//  that file; do not put a figure on this page that cannot be sourced.
//
//  Three things carried over from the old homepage that must not be lost,
//  because nothing else on the public site provides them:
//    · the portal door (top right) — the ONLY way a client or a pro reaches
//      their sign-in,
//    · Privacy Policy and Terms in the footer — carriers open these by hand
//      while reviewing our A2P text-messaging registration,
//    · the three dots at the very bottom — the way into the admin login.
//
//  Language: the portals stay fully translated. The marketing prose falls
//  through t() to the brandConfig copy, while buttons and footer reuse strings
//  that are already translated. Dropping `lp.*` keys into i18n.js translates
//  the prose too, with no change to this file.
// ═══════════════════════════════════════════════════════════════════════════

export default function Home({ variant = 'homeowners' }) {
  const { currentScreen, currentUser, goTo, setAuthForm } = useApp();
  const { t } = useI18n();
  // Same layout, two audiences. qpshub.com sells repairs to property
  // managers; qpshub.com/pros recruits the trades who do them. `prosSite`
  // spreads `site`, so the wordmark, the photos and the reviews link are
  // shared rather than kept in two places and allowed to drift.
  const isPros = variant === 'pros';
  const isManagers = variant === 'managers';
  const S = isPros ? { ...BRAND.site, ...BRAND.prosSite }
    : isManagers ? { ...BRAND.site, ...BRAND.managersSite }
      : BRAND.site;

  const refs = {
    trades: useRef(null),
    pros: useRef(null),
    how: useRef(null),
  };

  const toTop = (smooth) => window.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' });
  const jumpTo = (key) => refs[key]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // Leaving a long page has to leave the scroll behind with it: every other
  // screen is a sibling that's merely hidden, so a form opened from halfway
  // down the homepage would otherwise open halfway down itself.
  const leaveFor = (screen) => { toTop(false); goTo(screen); };

  // Every "Apply as a Pro" button on this page opens the SIGN-UP form, not the
  // sign-in form. The tech portal defaults to Sign In, which is right for a
  // technician coming back for their jobs and wrong for the only audience this
  // page has: someone who has never heard of us, clicking the biggest button
  // on the screen, and being asked for a password they do not have. Most of
  // them leave rather than hunt for the Create Account tab.
  //
  // The /apply and /join URLs already did this in App.js. The buttons did not.
  const applyAsPro = () => {
    setAuthForm((p) => ({ ...p, mode: 'register' }));
    leaveFor('screen-vendor-login');
  };

  const request = () => leaveFor('screen-request');

  // A footer link is somebody coming BACK. It always opens on Sign In, even
  // if the last thing they pressed on this page was Apply as a Pro — the
  // auth form keeps whichever mode it was left in, so without this the
  // footer would sometimes ask a returning technician to create a second
  // account.
  const signIn = (screen) => () => {
    setAuthForm((p) => ({ ...p, mode: 'login' }));
    leaveFor(screen);
  };

  // The big button on whichever page this is. A manager who has never heard
  // of us is not applying for anything — they have a leaking unit and want
  // somebody to come and fix it, so the front door opens the request form.
  // There is no self-serve company sign-up on purpose: Julian loads their
  // buildings for them, which is the offer the page actually makes.
  const apply = isPros ? applyAsPro : request;

  // A photo that hasn't been added yet draws its designed panel rather than a
  // broken image — see the note on `site.images` in brandConfig.js. These two
  // take a bare URL, which is what a pro's headshot further down still is.
  const media = (url) => (url ? { backgroundImage: `url(${url})` } : undefined);
  const cls = (url, base) => `${base}${url ? '' : ' empty'}`;

  // The `site.images` slots are `{ src, alt }` instead. They paint as CSS
  // backgrounds because each has to cover a panel of unknown shape, which costs
  // them the alt text an <img> gets for free — so it goes back on by hand. A
  // filled slot is a labelled role="img"; an empty one is decoration and stays
  // hidden, since "[ JOB PHOTO ]" is a note to us, not to a visitor. Without
  // this the banner and all four job tiles are silent to a screen reader and
  // invisible to Google, on the page pros are meant to find.
  const photo = (img) => media(img?.src);
  const photoCls = (img, base) => cls(img?.src, base);
  const photoLabel = (img) => (img?.src && img.alt
    ? { role: 'img', 'aria-label': img.alt }
    : { 'aria-hidden': 'true' });

  // Empty on day one, and deliberately so: three invented pros would be the
  // one lie this whole platform cannot afford. The founding-member panel
  // below takes the slot instead, and turns an empty roster into a reason to
  // move rather than a reason to leave.
  const pros = S.featuredPros || [];

  return (
    <div className={`screen ${currentScreen === (isPros ? 'screen-pros' : isManagers ? 'screen-managers' : 'screen-home') ? 'active' : ''}`}>
      <div className="qh">

        {/* ── Top rail ─────────────────────────────────────────────────── */}
        <nav className="qh-nav">
          <button type="button" className="qh-word" onClick={() => toTop(true)} aria-label={BRAND.name.full}>
            <Wordmark />
          </button>

          <div className="qh-links">
            <button type="button" className="qh-link" onClick={() => jumpTo('trades')}>{t('lp.nav.services', S.nav.services)}</button>
            {/* OUR WORK is a PAGE now, not a section on this one. Julian
                wants named portfolios he can fill himself and send one at a
                time — qpshub.com/work/decks to somebody asking about a deck
                — and a homepage carrying every finished job is a homepage
                nobody reaches the bottom of. */}
            <button type="button" className="qh-link" onClick={() => leaveFor('screen-work')}>{t('lp.nav.work', S.nav.work)}</button>
            <button type="button" className="qh-link" onClick={() => jumpTo('how')}>{t('lp.nav.about', S.nav.about)}</button>
            {S.reviewsUrl ? (
              <a className="qh-link" href={S.reviewsUrl} target="_blank" rel="noopener noreferrer">{t('lp.nav.reviews', S.nav.reviews)}</a>
            ) : null}
          </div>

          <div className="qh-navact">
            {currentUser && (
              <button type="button" className="qh-link" onClick={() => leaveFor(
                currentUser.role === 'admin' ? 'screen-admin-dashboard'
                  : currentUser.role === 'vendor' ? 'screen-vendor-dashboard'
                    : 'screen-homeowner-dashboard'
              )}>{t('nav.dashboard')}</button>
            )}
            <button type="button" className="qh-btn qh-btn-sm" onClick={apply}>{t('hero.cta', S.nav.cta)}</button>
            <ThemeToggle /><LanguageSwitcher />
            {/* Portal door → the chooser (client portal / pro portal). */}
            <button type="button" className="qh-icon" aria-label="Portals" onClick={() => leaveFor('screen-login-choice')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="4" width="16" height="7" rx="2.5" />
                <line x1="4" y1="15.5" x2="16" y2="15.5" />
                <line x1="4" y1="19.5" x2="12" y2="19.5" />
              </svg>
            </button>
          </div>
        </nav>

        {/* ── Banner ───────────────────────────────────────────────────── */}
        <header className="qh-banner">
          <div className={photoCls(S.images.banner, 'qh-banner-media')} style={photo(S.images.banner)} {...photoLabel(S.images.banner)} />
          <div className="qh-banner-scrim" />
          <div className="qh-banner-inner">
            <span className="qh-eyebrow">{t('lp.banner.trust', S.banner.trustLine)}</span>
            <h1 className="qh-h1 qh-disp">
              {t('lp.banner.headline', S.banner.headline)}
              <em>{t('lp.banner.headlineEm', S.banner.headlineEm)}</em>
            </h1>
            <p className="qh-lede">{t('lp.banner.blurb', S.banner.blurb)}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <button type="button" className="qh-btn" onClick={apply}>{t('hero.cta', S.banner.cta)}</button>
              {/* The second door. On the trades page it is the way out for
                  somebody who landed there wanting work done; on the manager
                  page the primary button already IS that, so the second one
                  points the other way — at the contractors we recruit. */}
              {isPros
                ? <button type="button" className="qh-btn-ghost" onClick={request}>Need work done</button>
                : <button type="button" className="qh-btn-ghost" onClick={() => leaveFor('screen-work')}>View Our Work</button>}
            </div>
          </div>
        </header>

        {/* ── The argument: three figures, each sourced in brandConfig ──── */}
        <section className="qh-stats">
          {(BRAND.stats || []).map((s, i) => (
            <div key={i} className="qh-stat">
              <b>{s.big}</b>
              <span>{s.small}</span>
            </div>
          ))}
        </section>

        {/* ── Why pros stay ────────────────────────────────────────────── */}
        <section className="qh-sec" ref={refs.how}>
          <div className="qh-sechead">
            <span className="qh-eyebrow">{isPros ? 'For trades' : isManagers ? 'For property managers' : 'For homeowners and property owners'}</span>
            <h2 className="qh-h2 qh-disp">{t('lp.standard.title', S.standard.title)}</h2>
          </div>
          <div className="qh-pillars">
            {(t('lp.standard.pillars', S.standard.pillars) || []).map((p, i) => (
              <div key={i} className="qh-pillar">
                <div className="qh-pillar-ico"><StandardIcon name={S.standard.pillars[i]?.icon} size={28} /></div>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Trades we place ──────────────────────────────────────────── */}
        <section className="qh-sec qh-sec-white" ref={refs.trades}>
          <div className="qh-sechead">
            <span className="qh-eyebrow">{isPros ? 'What we route' : 'What we take on'}</span>
            <h2 className="qh-h2 qh-disp">{t('lp.services.title', S.services.title)}</h2>
          </div>
          <div className="qh-trades">
            {(t('lp.services.items', S.services.items) || []).map((s, i) => (
              <button type="button" key={i} className="qh-trade" onClick={apply}>
                <b>{s.name}</b>
                <span>{s.body}</span>
              </button>
            ))}
          </div>
        </section>

        {/* ── Who's on the platform ────────────────────────────────────── */}
        <section className="qh-sec" ref={refs.pros}>
          <div className="qh-sechead">
            <span className="qh-eyebrow">{isPros ? 'On the platform' : 'Before anyone is sent'}</span>
            <h2 className="qh-h2 qh-disp">{t('lp.pros.title', S.pros.title)}</h2>
          </div>

          {pros.length > 0 ? (
            <div className="qh-pros">
              {pros.map((p, i) => (
                <div key={i} className="qh-pro">
                  <div className="qh-pro-face" style={media(p.photo)} />
                  <div className="qh-pro-body">
                    <div className="qh-pro-top">
                      <b>{p.name}<i>{p.trade}</i></b>
                      <span className="qh-dot" />
                    </div>
                    <div className="qh-pro-stats">
                      {(p.stats || []).map((st, j) => (
                        <div key={j} className="qh-pro-stat">
                          <b className={st.plain ? 'plain' : ''}>{st.big}</b>
                          <span>{st.small}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : isPros ? (
            <div className="qh-founding">
              <div className="qh-founding-txt">
                <h3>WE ARE ONBOARDING<br />OUR FIRST TRADES.</h3>
                <p>
                  The roster is open and the work is already booked. Early pros pick their
                  trades and their radius before anyone else is competing for the same jobs,
                  and their record starts building from the first one.
                </p>
                <button type="button" className="qh-btn" onClick={apply} style={{ alignSelf: 'flex-start' }}>
                  {t('hero.cta', S.nav.cta)}
                </button>
              </div>
              <div className="qh-seats">
                <b>25</b>
                <span>founding spots</span>
              </div>
            </div>
          ) : (
            // The same slot, for a manager. Recruiting copy here was the old
            // page showing through: "25 founding spots" is an offer to a
            // contractor and means nothing to somebody with a leaking unit.
            //
            // Every sentence is a thing the app actually enforces. The
            // licence and insurance checks and the expiry gate behind them
            // are real (server/compliance.js), and a technician whose
            // paperwork lapses cannot be paid until it is current.
            //
            // Two things Julian took OUT of this paragraph, deliberately:
            // naming Utah DOPL, and the W-9. The checks still happen and the
            // W-9 is still collected — the pros page still asks a technician
            // for one — but a customer reading this wants to know somebody
            // checked, not which registry was searched or which tax form is
            // on file.
            <div className="qh-founding">
              <div className="qh-founding-txt">
                <h3>EVERY TRADE IS<br />CHECKED FIRST.</h3>
                <p>
                  Licence and insurance verified before anyone is sent to you, and checked
                  again as each one comes up for renewal. If either lapses, that technician
                  stops being paid until it is current. Nobody arrives at your building on a
                  promise.
                </p>
                <button type="button" className="qh-btn" onClick={apply} style={{ alignSelf: 'flex-start' }}>
                  {t('hero.cta', S.nav.cta)}
                </button>
              </div>
              <div className="qh-seats">
                <b>1,800+</b>
                <span>jobs invoiced</span>
              </div>
            </div>
          )}
        </section>

        {/* The work used to be four photo slots here, typed into
            brandConfig.js, so adding a finished job meant a code change and
            a deploy. It is screens/Work.js now: a page of its own that
            Julian fills from the admin screen, grouped into named
            portfolios, each with its own address. Both doors to it are
            above — the rail and the banner's second button. */}

        {/* ── Getting on the platform ──────────────────────────────────── */}
        <section className="qh-sec qh-sec-dark">
          <div className="qh-sechead">
            <span className="qh-eyebrow">Four steps</span>
            <h2 className="qh-h2 qh-disp">{t('lp.process.title', S.process.title)}</h2>
          </div>
          <div className="qh-steps">
            {(t('lp.process.steps', S.process.steps) || []).map((s, i) => (
              <div key={i} className="qh-step">
                <b>{String(i + 1).padStart(2, '0')}</b>
                <strong>{s.label}</strong>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Closing ──────────────────────────────────────────────────── */}
        <section className="qh-close">
          <h2 className="qh-h2 qh-disp">
            {t('lp.closing.line1', S.closing.line1)}<br />
            {t('lp.closing.line2', S.closing.line2)}
          </h2>
          <button type="button" className="qh-btn-dark" onClick={apply}>
            {t('lp.closing.cta', S.closing.cta)}
          </button>
        </section>

        {/* ── Footer ───────────────────────────────────────────────────── */}
        <footer className="qh-foot">
          <Wordmark className="qh-foot-word" />
          <span className="qh-foot-sp">
            {BRAND.company.website} · {t('brand.location', BRAND.location)} · © {BRAND.copyrightYear} {BRAND.company.legalName}
          </span>
          {/* Carriers reviewing our A2P text-messaging registration open these
              links by hand, so they have to be reachable from the public site. */}
          {/* ── The three front doors to the app ─────────────────────
              Sign-in links, not marketing pages. Somebody reading the
              footer is looking for the way IN — they already know who we
              are, they are on their phone, and they want their jobs.

              The marketing pages they came from are /pros and /managers,
              which are the addresses Julian hands out, not ones anybody
              goes hunting for in a footer. */}
          <button type="button" className="qh-foot-link" onClick={signIn('screen-homeowner-login')}>Homeowners</button>
          <button type="button" className="qh-foot-link" onClick={signIn('screen-account-login')}>Property Managers &amp; Landlords</button>
          {/* Opens on SIGN IN, not Create Account. A technician reading a
              footer is coming back for their jobs; recruiting has its own
              page and its own button. */}
          <button type="button" className="qh-foot-link" onClick={signIn('screen-vendor-login')}>Trade Partners</button>
          <button type="button" className="qh-foot-link" onClick={() => leaveFor('screen-privacy')}>Privacy Policy</button>
          <button type="button" className="qh-foot-link" onClick={() => leaveFor('screen-terms')}>Terms of Service</button>
          <div className="qh-dots" onClick={() => leaveFor('screen-admin-login')}>· · ·</div>
        </footer>

      </div>
    </div>
  );
}
